import { createGlobalStyle } from "styled-components";
import reset from "reset-css";


// Font awesome icons at:
// https://react-icons.github.io/react-icons/icons?name=fa

const size = {
  mobileS: "320px",
  mobileM: "375px",
  mobileL: "425px",
  tablet: "768px",
  laptop: "1024px",
  laptopM: "1200px",
  laptopL: "1440px",
  desktop: "2560px",
};

export const device = {
  mobileS: `(min-width: ${size.mobileS})`,
  mobileM: `(min-width: ${size.mobileM})`,
  mobileL: `(min-width: ${size.mobileL})`,
  tablet: `(min-width: ${size.tablet})`,
  laptop: `(min-width: ${size.laptop})`,
  laptopM: `(min-width: ${size.laptopM})`,
  laptopL: `(min-width: ${size.laptopL})`,
  desktop: `(min-width: ${size.desktop})`,
  desktopL: `(min-width: ${size.desktop})`,
};

export default createGlobalStyle`
  ${reset}


html {
    /* Confetti & Coral palette */
    --color-coral:        #FF6B6B;
    --color-coral-deep:   #E8484F;
    --color-coral-soft:   #FFA89A;
    --color-plum:         #3D1E5C;
    --color-plum-light:   #6B3FA0;
    --color-gold:         #F4C95D;
    --color-cream:        #FFF8F0;
    --color-pink-soft:    #FFE4E1;
    --color-pink-blush:   #FCEDEA;

    /* Surfaces */
    --color-background-white: #ffffff;
    --color-background-grey:  #FFF8F0;
    --color-surface:          #ffffff;

    /* Legacy aliases (mapped to new palette so existing components stay coherent) */
    --color-accent-prim:   var(--color-coral);
    --color-accent-sec:    var(--color-gold);
    --color-accent-tir:    var(--color-plum);
    --color-accent-purple: var(--color-coral);
    --color-accent-orange: var(--color-coral-soft);
    --color-accent-pink:   var(--color-pink-blush);

    /* Text */
    --color-text:        #1B0D2E;
    --color-text-medium: var(--color-plum);
    --color-text-soft:   #6B5B7A;
    --color-text-light:  #ffffff;

    --color-white:       #ffffff;
    --color-black:       #000000;
    --color-light-grey:  #F6F1EC;
    --color-medium-grey: #E5DCD3;
    --color-dark-grey:   #9C8E9F;

    /* Status */
    --color-status-new:     #3A3A3A;
    --color-status-good:    #1bb775;
    --color-status-bad:     #d62809;
    --color-status-warning: #d98a02;

    /* Effects */
    --shadow-soft:   0 6px 18px rgba(61, 30, 92, 0.08);
    --shadow-medium: 0 14px 40px rgba(61, 30, 92, 0.12);
    --shadow-lift:   0 22px 60px rgba(232, 72, 79, 0.18);
    --radius-sm: 12px;
    --radius-md: 18px;
    --radius-lg: 28px;
    --radius-xl: 40px;

    --gradient-festive: linear-gradient(135deg, #FFD3C4 0%, #FFA89A 35%, #FF6B6B 70%, #E8484F 100%);
    --gradient-soft:    linear-gradient(180deg, #FFF8F0 0%, #FCEDEA 100%);
}

:root {
    position: relative;
    min-height: calc(100vh - 64px);
    width: 100%;
    margin: auto;
    color: var(--color-text);
    font-family: 'Inter', 'Montserrat', sans-serif;
}

html, *{box-sizing: border-box;}
*,*:before,*:after {box-sizing: inherit;}

::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 8px;
}

::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background-color: var(--color-coral-soft);
  box-shadow: 0 0 1px rgba(255, 255, 255, .5);
}

body{
  background: var(--gradient-soft);
  background-attachment: fixed;
  color: var(--color-text);
  font-family: 'Inter', 'Montserrat', sans-serif;
  text-align: left;
  font-size: 1rem;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow-x: hidden;
}

/*-------main_style-------*/
a {
  text-decoration: none;
  color: inherit;
  transition: color .2s ease;
  &:hover{
    color: var(--color-coral);
  }
  &:active{
    color: var(--color-coral-deep);
  }
}

/*-------Headers-------*/
h1, h2, h3, h4 {
  font-family: 'Fraunces', 'Barlow Condensed', serif;
  color: var(--color-plum);
  letter-spacing: -0.01em;
}

h1 {
  font-weight: 700;
  font-size: clamp(2.4rem, 5vw, 3.6rem);
  line-height: 1.05;
  text-transform: none;
}

h2 {
  font-weight: 600;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  line-height: 1.15;
  a {
    font-size: 1.4rem;
  }
}

h3 {
  font-weight: 600;
  font-size: 1.4rem;
  line-height: 1.25;
}

h4 {
  font-weight: 500;
  font-size: 1.15rem;
  line-height: 1.3;
}

p {
  color: var(--color-text-soft);
  line-height: 1.65;
}

i{
  font-style: italic;
}

/*-------MUI overrides-------*/
.MuiBadge-colorSecondary {
  color: #fff;
  background-color: var(--color-coral) !important;
}
.MuiStepIcon-root.MuiStepIcon-active {
  color: var(--color-coral);
}
.MuiStepIcon-root.MuiStepIcon-completed {
  color: var(--color-gold);
}
.MuiCardContent-root {
    min-height: 6rem;
}
.MuiInput-underline:after {
    border-bottom: 2px solid var(--color-coral);
}
.MuiFormLabel-root.Mui-focused {
  color: var(--color-coral);
}
.MuiSlider-root {
  color: var(--color-coral);
}
.MuiCheckbox-colorSecondary.Mui-checked {
  color: var(--color-coral);
}
.MuiCheckbox-colorSecondary:hover, .MuiCheckbox-colorSecondary.Mui-checked:hover {
  background-color: rgba(255, 107, 107, 0.08);
}
.MuiButton-contained{
  background-color: var(--color-medium-grey);
  :hover{
    background-color: var(--color-dark-grey);
  }
}

.MuiIconButton-root{
  color: inherit;
}

.MuiIconButton-label{
  text-align: left;
}

.MuiButton-root:hover {
  text-decoration: none;
  background-color: initial;
}

/*-------CUSTOM-------*/
.required:after {
    content:" *";
    color: var(--color-coral);
}

.customButton {
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  border: none;
  border-radius: 999px;
  min-width: 120px;
  padding: 0.55rem 1.25rem;
  color: var(--color-white);
  margin-right: 0.75rem;
  background-color: var(--color-coral);
  box-shadow: 0 6px 18px rgba(232, 72, 79, 0.25);
  transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease;
  text-transform: none;
  :hover{
    background-color: var(--color-coral-deep);
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(232, 72, 79, 0.35);
    opacity: 1;
  }
}

.paymentButton {
  font-size: 16px;
}

.customButton2 {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  border: 1.5px solid rgba(61, 30, 92, 0.12);
  border-radius: 999px;
  min-width: 120px;
  padding: 0.5rem 1.2rem;
  color: var(--color-plum);
  background-color: var(--color-white);
  margin-right: 0.75rem;
  transition: transform .18s ease, border-color .18s ease, color .18s ease;
  text-transform: none;
  :hover{
    background-color: var(--color-white);
    border-color: var(--color-coral);
    color: var(--color-coral);
    transform: translateY(-1px);
    opacity: 1;
  }
}

.customButton3 {
  min-width: 120px;
  border: 1.5px solid var(--color-white);
  color: var(--color-white);
  margin-right: 1rem;
  border-radius: 999px;
  padding: 0.5rem 1.2rem;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  :hover{
    background-color: rgba(255,255,255,0.12);
    opacity: 1;
  }
}

.small-text{
  font-size: .8rem;
}

.colorText{
  color: var(--color-plum);
}

::selection {
  background: var(--color-coral-soft);
  color: var(--color-plum);
}
`;
